import {get, map, transform} from 'lodash';
import moment, {Moment} from 'moment';
import {useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {Chart} from 'components';
import {UserContext} from 'context';
import {ActivityStatistic, SessionHistoryFullStatistic} from 'interfaces';
import {dateUtils} from 'utils';
import {formatSeconds} from 'utils/transform';

import {REQUEST_SESSION_HISTORY_DATE_FORMAT} from '../../constants';
import {ActivityMetric, ActivityMetricColor} from '../../enums';
import {ActivityMetricConfig} from '../../types';

export enum AggregationType {
    Hourly = 'hourly',
    Daily = 'daily',
    Monthly = 'monthly'
}

const MONTHLY_FORMAT = 'MM-YYYY';

type Props = {
    statistic: SessionHistoryFullStatistic;
    metrics: ActivityMetric[];
    startDate: Moment;
    endDate: Moment;
    configs: ActivityMetricConfig[];
    duration?: boolean;
    aggregationType?: AggregationType;
};

export const ActivityMetricsChart: React.FC<Props> = ({
    duration,
    startDate,
    endDate,
    metrics,
    configs,
    statistic,
    aggregationType = AggregationType.Daily
}: Props) => {
    const {t} = useTranslation();
    const {settings: {common: {dateFormat, timeFormat}}} = useContext(UserContext);
    const [dates, setDates] = useState([]);
    const [yAxis, setYAxis] = useState({});
    const [xAxis, setXAxis] = useState({});
    const [chartData, setChartData] = useState([]);

    const dateFormats = useMemo(() => ({
        hourly: timeFormat,
        monthly: MONTHLY_FORMAT
    }), [timeFormat]);

    const isHourly = useMemo(() => aggregationType === AggregationType.Hourly, [aggregationType]);
    const isDaily = useMemo(() => aggregationType === AggregationType.Daily, [aggregationType]);
    const isMonthly = useMemo(() => aggregationType === AggregationType.Monthly, [aggregationType]);

    useEffect(() => {
        setYAxis(duration ? {tickFormatter: (value) => formatSeconds(value), width: 100} : {});
    }, [duration]);

    useEffect(() => {
        setDates(dateUtils.getRange(
            startDate,
            endDate,
            aggregationType === AggregationType.Daily ? 'days' : 'months',
            REQUEST_SESSION_HISTORY_DATE_FORMAT,
            {includeEndDate: !isMonthly || !startDate.isSame(endDate, 'month')}));
    }, [startDate, endDate, aggregationType, isMonthly]);

    const defaultValue = useMemo(() => transform(configs, (acc, config) => acc[config.metric] = 0, {}), [configs]);

    const getMetricsChartData = useCallback((statisticPeriod: ActivityStatistic) =>
        !statisticPeriod ? defaultValue : transform(configs, (acc, config) => {
            if (!metrics.includes(config.metric)) {
                return;
            }
            acc[config.metric] = config.valueGetter(statisticPeriod) || defaultValue[config.metric];
        }, []),
    [configs, metrics, defaultValue]);

    const getDailyChartData = useCallback(() =>
        map(dates, (date) => ({
            date: moment(date, REQUEST_SESSION_HISTORY_DATE_FORMAT).format(dateFormat),
            ...(getMetricsChartData(statistic.days[date]))
        })), [statistic, dates, getMetricsChartData, dateFormat]);

    const getHourlyChartData = useCallback(() => {
        const data = [];
        const date = moment();
        const startOfDay = date.clone().startOf('day');
        const endOfDay = date.clone().endOf('day');
        const days = dateUtils.getRange(startOfDay, endOfDay, 'hours', dateFormats.hourly, {includeEndDate: true});

        for (let i = 0; i < 24; i++) {
            data.push(transform(configs, (acc, {metric}) => {
                const hoursMetric = get(statistic.total.hours, metric) || [];
                acc[metric] = hoursMetric[i] || 0;
            }, {name: days[i]}));
        }
        return data;
    }, [dateFormats.hourly, configs, statistic.total.hours]);

    const getMonthlyChartData = useCallback(() =>
        map(dates, (date) => {
            const monthDate = moment(date, REQUEST_SESSION_HISTORY_DATE_FORMAT).format(dateFormats.monthly);
            return {
                date: monthDate,
                ...(getMetricsChartData(statistic.months[monthDate]))
            };
        }), [dates, dateFormats.monthly, getMetricsChartData, statistic.months]);

    useEffect(() => {
        if (isDaily && statistic?.days) {
            setChartData(getDailyChartData());
            setXAxis({dataKey: 'date'});
        } else if (isMonthly && statistic?.months) {
            setChartData(getMonthlyChartData());
            setXAxis({dataKey: 'date'});
        } else if (isHourly && statistic?.total.hours) {
            setChartData(getHourlyChartData());
            setXAxis({dataKey: 'name'});
        } else {
            setChartData([]);
        }
    }, [dates, statistic, isDaily, isHourly, isMonthly, getDailyChartData, getHourlyChartData, getMonthlyChartData]);

    const [lines, setLines] = useState([]);

    useEffect(() => {
        const linesConfig = configs.map(({metric, title}) => ({
            dataKey: metrics.includes(metric) ? metric : '',
            stroke: ActivityMetricColor[metric],
            name: t(title),
            formatter: duration ? (value) => formatSeconds(value) : null
        }));
        setLines(map(linesConfig, (config) => ({type: 'linear', dot: false, ...config})));
    }, [t, configs, metrics, duration, isHourly]);

    const tooltip = useMemo(() =>
        isHourly ? ({
            labelFormatter: (value) => {
                const date = moment(value, dateFormats.hourly);
                const nextDate = date.add({h: 1}).format(dateFormats.hourly);
                return `${value} - ${nextDate}`;
            }
        }) : {},
    [dateFormats.hourly, isHourly]);

    return (
        <Chart
            tooltip={tooltip}
            margin={{top: 20, right: 20, left: -10, bottom: 5}}
            grid={{stroke: '#eeeeee'}}
            xAxis={xAxis}
            yAxis={yAxis}
            lines={lines}
            data={chartData}
        />
    );
};
